import { omit, isEmpty, toLower } from 'lodash';
import { axios } from './common';
import {
  CM_BASE_URL,
  STR_POLLING_INTERVAL_MS
} from '../../pages/constants/cmConstants';
import { downloadFile } from '../../pages/cm/utils/common';
import { convertLocalEpochToUTC, getLocalTimezone } from '../../util/timezone';

export const fetchCases = async ({ filters, currentPage, pageSize }) => {
  const params = {
    'alert-level': filters.alertLevel,
    'assigned-user': !isEmpty(filters?.assignedUser)
      ? filters.assignedUser.join(',')
      : null,
    ...(filters.approvalStatus
      ? { 'checker-status': filters.approvalStatus }
      : ''),
    typologyIds: !isEmpty(filters.typologyIds)
      ? filters.typologyIds.join(',')
      : null,
    sources: !isEmpty(filters.sourceIds) ? filters.sourceIds.join(',') : null,
    ...omit(filters, [
      'alertLevel',
      'assignedUser',
      'searchField',
      'searchText',
      'typologyIds',
      'approvalStatus',
      'sourceIds'
    ]),
    offset: (currentPage - 1) * pageSize,
    limit: pageSize,
    from: convertLocalEpochToUTC(filters.from),
    to: convertLocalEpochToUTC(filters.to)
  };

  if (filters.searchField && filters.searchText) {
    params[filters.searchField] = toLower(filters.searchText);
  }

  const url = `${CM_BASE_URL}/cases/all`;
  const res = await axios.get(url, { params });

  if (res.status !== 200) {
    const error = res?.data?.error;

    throw { message: error ?? 'Failed to fetch cases' };
  }

  return res.data;
};

export const fetchCaseDetailsByCaseId = async (caseId) => {
  const url = `${CM_BASE_URL}/cases/byId/${caseId}`;
  const res = await axios.get(url);

  if (res.status !== 200) {
    throw new Error(res?.data?.error ?? 'Failed to fetch case details');
  }

  return res.data;
};

export const caseActionSubmit = async ({ caseActionDetails }) => {
  const url = `${CM_BASE_URL}/cases/takeActionOnCase`;

  return axios.post(url, caseActionDetails).then((res) => {
    if (res.status !== 200) {
      throw { message: 'Failed to submit the action' };
    }
    return res.data;
  });
};

export const fetchCasesAllowedStatuses = async (caseIds) => {
  const params = {
    caseIds
  };
  const url = `${CM_BASE_URL}/cases/validCaseStatus`;

  return axios.post(url, params).then((res) => {
    if (res.status !== 200) {
      throw { message: 'Failed to fetch allowed case status transitions' };
    }
    return res.data;
  });
};

export const submitCasesStatus = async ({ caseIds, status }) => {
  const requestBody = {
    caseId: caseIds,
    status
  };

  const url = `${CM_BASE_URL}/cases/updateCaseStatus`;

  return axios.put(url, requestBody).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw { message: 'Failed to update case status' };
    }
    return res.data;
  });
};

export const assignCaseToUser = async ({ caseIds, user }) => {
  const requestBody = {
    caseIds,
    user
  };

  const url = `${CM_BASE_URL}/cases/assignToUser`;

  return axios.put(url, requestBody).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw new Error('Something went wrong. Please try again after sometime.');
    }
    return res.data;
  });
};

export const updateCaseName = async ({ caseId, caseName }) => {
  const url = `${CM_BASE_URL}/cases/${caseId}/name`;

  const params = {
    name: caseName
  };

  return axios.put(url, params).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw { message: 'Failed to update the case name' };
    }
    return res.data;
  });
};

export const createCase = async ({
  name,
  note,
  customers,
  dept,
  customFields
}) => {
  const requestBody = {
    name,
    note,
    customers,
    dept,
    customFields
  };

  const url = `${CM_BASE_URL}/cases`;

  return axios.post(url, requestBody).then((res) => {
    if (res.status !== 200 || !res.data.caseId) {
      const error = res?.data?.error;
      throw new Error(error ?? 'Error creating a case');
    }
    return res.data;
  });
};

export const updateCase = async (caseDetails) => {
  const url = `${CM_BASE_URL}/cases/${caseDetails.caseId}`;
  const requestBody = omit(caseDetails, ['caseId']);

  return axios.put(url, requestBody).then((res) => {
    if (res.status !== 200 || !res.data.caseId) {
      const error = res?.data?.error;
      throw new Error(error ?? 'Error updating a case');
    }
    return res.data;
  });
};

export const downloadSTRReport = (
  documentId,
  caseId,
  onGetStrSuccess,
  onGetStrError
) => {
  const fileUrl = `${CM_BASE_URL}/cases/getSTR?docId=${documentId}`;
  axios({
    url: fileUrl,
    method: 'GET',
    responseType: 'blob'
  }).then((response) => {
    //status 202: download still in progress
    if (response.status === 202) {
      setTimeout(
        () =>
          downloadSTRReport(documentId, caseId, onGetStrSuccess, onGetStrError),
        STR_POLLING_INTERVAL_MS
      );
    } else if (response.status === 200) {
      downloadFile(response.data, `Investigation Report - ${caseId}.pdf`);
      onGetStrSuccess(caseId);
    } else {
      onGetStrError();
      throw { message: 'Failed to download STR' };
    }
  });
};

export const generateSTRReport = (caseId, alertIds, onCreateStrSuccess) => {
  const requestBody = {
    caseId: parseInt(caseId),
    alertIds
  };
  const url = `${CM_BASE_URL}/cases/createSTR`;
  axios
    .post(url, requestBody, { headers: { 'Time-Zone': getLocalTimezone() } })
    .then((res) => {
      if (res.status !== 200 || !res.data.documentId) {
        throw { message: 'Failed to generate STR' };
      } else {
        onCreateStrSuccess(res.data.documentId, caseId);
      }
    });
};
